import React, { useEffect, useMemo, useState } from "react";
import LibraryFormModal from "../components/LibraryFormModal";
import LibraryField, {
  librarySelectClass,
} from "../components/LibraryField";
import { ChevronDownIcon } from "../../../Icons";
import { getRoles } from "../../../Auth";

const EMPTY = {
  formCreatorRoleName: "",
  formSignerRoleName: "",
  status: "Active",
};

const ClinicalCosignerFormModal = ({ isOpen, record, onClose, onSave }) => {
  const isEdit = Boolean(record?.id);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSaving(false);
    setForm(
      record
        ? {
            formCreatorRoleName: record.formCreatorRoleName || "",
            formSignerRoleName: record.formSignerRoleName || "",
            status: record.status || "Active",
          }
        : EMPTY,
    );
    getRoles().then((list) => setRoles(Array.isArray(list) ? list : []));
  }, [isOpen, record]);

  const roleOptions = useMemo(
    () =>
      (roles || [])
        .map((r) => r.name || r.code)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [roles],
  );

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSubmit = async () => {
    const nextErrors = {};
    if (!form.formCreatorRoleName) {
      nextErrors.formCreatorRoleName = "Form creator role is required";
    }
    if (!form.formSignerRoleName) {
      nextErrors.formSignerRoleName = "Form signer role is required";
    }
    if (
      form.formCreatorRoleName &&
      form.formSignerRoleName &&
      form.formCreatorRoleName === form.formSignerRoleName
    ) {
      nextErrors.formSignerRoleName =
        "Form creator and form signer roles must be different";
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setSaving(true);
    try {
      await onSave?.({
        formCreatorRoleName: form.formCreatorRoleName,
        formSignerRoleName: form.formSignerRoleName,
        status: form.status,
      });
      onClose?.();
    } catch (err) {
      setErrors({ form: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <LibraryFormModal
      isOpen={isOpen}
      title={isEdit ? "Edit Clinical Co-signer" : "Create Clinical Co-signer"}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSaving={saving}
      submitLabel={isEdit ? "Update" : "Create"}
    >
      {errors.form ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {errors.form}
        </p>
      ) : null}

      <p className="text-[12px] text-slate-600 leading-relaxed">
        Role-based mapping: any employee in the creator role (e.g. OTA) fills
        the form with <strong>Save</strong> + <strong>To Be Sign</strong>. Any
        employee in the signer role (e.g. OT) can then open it, sign, and use{" "}
        <strong>Sent to Office</strong>.
      </p>

      <LibraryField
        id="cosign-creator-role"
        label="Form Creator Role"
        required
        error={errors.formCreatorRoleName}
        hint="e.g. PTA / OTA — role that fills the form"
      >
        <div className="relative">
          <select
            id="cosign-creator-role"
            className={librarySelectClass(errors.formCreatorRoleName)}
            value={form.formCreatorRoleName}
            onChange={(e) => setField("formCreatorRoleName", e.target.value)}
          >
            <option value="">Select role</option>
            {roleOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <ChevronDownIcon
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>
      </LibraryField>

      <LibraryField
        id="cosign-signer-role"
        label="Form Signer Role"
        required
        error={errors.formSignerRoleName}
        hint="e.g. PT / OT — role that co-signs"
      >
        <div className="relative">
          <select
            id="cosign-signer-role"
            className={librarySelectClass(errors.formSignerRoleName)}
            value={form.formSignerRoleName}
            onChange={(e) => setField("formSignerRoleName", e.target.value)}
          >
            <option value="">Select role</option>
            {roleOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <ChevronDownIcon
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>
      </LibraryField>

      <LibraryField id="cosign-status" label="Status">
        <div className="relative">
          <select
            id="cosign-status"
            className={librarySelectClass()}
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <ChevronDownIcon
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>
      </LibraryField>
    </LibraryFormModal>
  );
};

export default ClinicalCosignerFormModal;
